import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AccordionBox from "components/Layouts/AccordionBox";
import ConfirmDialog from "components/Elements/ConfirmDialog";
import { CircularProgress, Box } from "@mui/material";
import ScheduleReportModal from "../ScheduleReportModal";
import { useScheduleReportModal } from "../hooks/useScheduleReportModal";
import { useReportSubscriptions } from "../hooks/useReportSubscriptions";
import { formValuesToApiRequest } from "services/reportSubscriptions";
import ReportSubscriptionsExplanation from "./ReportSubscriptionsExplanation";
import ReportSubscriptionsEmptyState from "./ReportSubscriptionsEmptyState";
import ReportSubscriptionsList from "./ReportSubscriptionsList";

// Page-level glue for the profile subscriptions card. Loads subscriptions
// from the API via useReportSubscriptions and forwards user actions to the
// hook's create/update/toggle/remove operations.
const ReportSubscriptionsCard = () => {
	const { t } = useTranslation();
	const { subscriptions, loading, update, toggle, remove } = useReportSubscriptions();
	const [pendingDelete, setPendingDelete] = useState(null);
	const editModal = useScheduleReportModal();
	const location = useLocation();
	const autoOpenedRef = useRef(false);

	const handleToggle = useCallback((id) => toggle(id), [toggle]);

	const handleDeleteRequest = useCallback(
		(id) => {
			const sub = subscriptions.find((s) => s.id === id);
			if (sub) setPendingDelete(sub);
		},
		[subscriptions]
	);

	const cancelDelete = useCallback(() => setPendingDelete(null), []);

	const confirmDelete = useCallback(async () => {
		if (!pendingDelete) return;
		const ok = await remove(pendingDelete.id);
		if (ok) setPendingDelete(null);
	}, [pendingDelete, remove]);

	const handleEdit = useCallback(
		(subscription) => {
			editModal.openModal({
				name: subscription.name,
				reportType: subscription.reportType,
				frequency: subscription.frequency,
				daysOfWeek: subscription.daysOfWeek,
				timeOfDay: subscription.timeOfDay,
				timezone: subscription.timezone,
				deliveryEmail: subscription.deliveryEmail,
				_id: subscription.id,
				_reportName: subscription.reportName,
				_filterContext: subscription.filterContext,
				_filterSummary: subscription.filters,
			});
		},
		[editModal]
	);

	// Deep link from report emails: /app/me?subscriptionId=<id> auto-opens that
	// subscription for editing/unsubscribing. The siteApp-switch HOC has already
	// resolved tenant context by the time this list loads, so the id resolves
	// against the correct tenant's subscriptions. Fires once per mount.
	useEffect(() => {
		if (autoOpenedRef.current || loading) return;
		// Loading has finished — this is our one shot regardless of whether the
		// id resolves. Set the guard up-front so a stale/deleted/wrong-tenant
		// link doesn't cause the effect to retry on every later re-render
		// (e.g. a reload triggered by another action).
		autoOpenedRef.current = true;
		const targetId = new URLSearchParams(location.search).get("subscriptionId");
		if (!targetId) return;
		const sub = subscriptions.find((s) => String(s.id) === String(targetId));
		if (!sub) return;
		handleEdit(sub);
	}, [loading, subscriptions, location.search, handleEdit]);

	const handleEditSubmit = useCallback(
		async (payload) => {
			const id = editModal.initialValues?._id;
			const filterContext = editModal.initialValues?._filterContext;
			const filterSummary = editModal.initialValues?._filterSummary;
			const apiBody = formValuesToApiRequest(payload, filterContext, filterSummary);
			await update(id, apiBody);
		},
		[editModal.initialValues, update]
	);

	const editingReports = editModal.initialValues
		? [
				{
					id: editModal.initialValues._id,
					name: editModal.initialValues._reportName,
					reportType: editModal.initialValues.reportType,
				},
		  ]
		: [];

	return (
		<>
			<AccordionBox title={t("title.reportSubscriptions")} noExpand>
				<ReportSubscriptionsExplanation />
				{loading ? (
					<Box display="flex" justifyContent="center" p={3}>
						<CircularProgress size={24} />
					</Box>
				) : subscriptions.length === 0 ? (
					<ReportSubscriptionsEmptyState />
				) : (
					<ReportSubscriptionsList
						subscriptions={subscriptions}
						onToggle={handleToggle}
						onEdit={handleEdit}
						onDelete={handleDeleteRequest}
					/>
				)}
			</AccordionBox>
			{editModal.open ? (
				<ScheduleReportModal
					open
					onClose={editModal.closeModal}
					availableReports={editingReports}
					initialValues={editModal.initialValues || undefined}
					mode="edit"
					lockReportSelection
					onSubmit={handleEditSubmit}
				/>
			) : null}
			<ConfirmDialog
				open={!!pendingDelete}
				title={t("title.deleteSubscription")}
				message={
					pendingDelete
						? t("message.subscriptionWillStopBeingDelivered", {
								name: pendingDelete.name || pendingDelete.reportName,
						  })
						: ""
				}
				confirmButtonText={t("action.delete")}
				onConfirm={confirmDelete}
				onCancel={cancelDelete}
			/>
		</>
	);
};

export default ReportSubscriptionsCard;
